/**
 * Pure helpers for the header search history. History is now ACCOUNT-based (server is the
 * source of truth); this module owns only the de-dup key + the per-user localStorage cache
 * that makes reads instant. All server sync lives in store/modules/search_history.js.
 *
 * Item shape:
 *   { type: 'brand' | 'category' | 'product' | 'term',
 *     label: string,
 *     navKey?: 'brand' | 'sub_brand' | 'department' | 'category', // taxonomy → /products query key
 *     id?: number,                 // taxonomy id (brand/category rows)
 *     query?: { brand?, sub_brand?, department?, category? }, // full ancestor chain — taxonomy rows
 *     designId?: string,           // complete design (base + colour) — product rows
 *     designNumber?: string,       // base design — product rows
 *     productId?: number,          // product rows
 *     term?: string }              // free-text rows
 */

export const HISTORY_CAP = 20

const normalize = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase()

/**
 * Canonical taxonomy order. Mirrors PageHeader::keyFor()'s slot-per-level shape and
 * ProductRepository::TAXONOMY_LEVELS, spelled in products-page query-param names.
 * Iterated explicitly so the key never depends on object property order, which would
 * otherwise vary with how the payload was built or round-tripped through JSON.
 */
const CHAIN_LEVELS = ['brand', 'sub_brand', 'department', 'category']

/**
 * Deterministic de-dup key, per type (Gucci/gucci/GUCCI must not duplicate). This is the
 * SOLE author of a row's identity: the client sends it to the server as `dedup_key`, which
 * the server stores verbatim, so client and server keys always match.
 *
 * Taxonomy rows key on the whole ancestor chain, not the matched id: department and
 * category rows are shared across brands, so "Bocara / Neckwear" and "Geoff Nicholson /
 * Blue Label / Neckwear" carry the same department id and would otherwise collapse into
 * one recent-search row. Rows stored before the chain existed keep the old key and stay
 * replayable.
 */
export const historyKey = (item) => {
  if (!item || !item.type) return ''
  if (item.type === 'term') return `term:${normalize(item.term)}`
  if (item.type === 'product') return `product:${normalize(item.designId || item.designNumber || item.productId)}`
  if (item.query) return `node:${CHAIN_LEVELS.map((level) => item.query[level] || '').join(':')}`
  return `${item.navKey || item.type}:${item.id}`
}

/** Newest-first, de-duplicated (first occurrence wins), capped. */
export const dedupeNewestFirst = (items) => {
  const seen = new Set()
  const out = []
  for (const item of items || []) {
    const key = historyKey(item)
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(item)
  }
  return out.slice(0, HISTORY_CAP)
}

export const upsert = (items, item) => dedupeNewestFirst([item, ...(items || [])])

export const removeByKey = (items, key) => (items || []).filter((item) => historyKey(item) !== key)

// ---- per-user localStorage cache (instant reads; survives reload) ----

const cacheKey = (userId) => `ps.searchHistory.${userId != null ? userId : 'anon'}`

export const readCache = (userId) => {
  try {
    const raw = window.localStorage.getItem(cacheKey(userId))
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

export const writeCache = (userId, items) => {
  try {
    window.localStorage.setItem(cacheKey(userId), JSON.stringify(items))
  } catch (e) {
    // Private mode / quota — history is best-effort, so swallow.
  }
}

export const clearCache = (userId) => {
  try {
    window.localStorage.removeItem(cacheKey(userId))
  } catch (e) {}
}
